/// <amd-module name="Build/Pipelines/Components/StageGraph" />
import * as React from "react";

import { FPSLink } from "VSS/Features/PlatformUI/FPSLink";
import { VssComponent } from "VSS/Platform/Layout";

import { IStatusProps, Status, StatusSize } from "VSSUI/Status";
import { Tooltip } from "VSSUI/TooltipEx";
import { css } from "VSSUI/Util";
import { Location } from "VSSUI/Utilities/Position";

export interface StageGraphNode {
    status: IStatusProps | undefined;
    hiddenStageCount?: number;
    tooltip?: string;
    href?: string;
    ariaLabel?: string;
    telemetrySource?: string;
    telemetryProperties?: {};
    onClick?: () => void;
}

export interface StageGraphProps {
    stages: StageGraphNode[][][];
    size: StatusSize;
}

export class StageGraph extends React.Component<StageGraphProps> {
    public render(): JSX.Element {
        return <div className="flex-row scroll-hidden">
            {this.props.stages.map((stages, index) => {
                const isParallel = stages.length > 1;
                const nextIsParallel = (index < this.props.stages.length - 1) && this.props.stages[index + 1].length > 1;
                const color = stages[stages.length - 1][stages[stages.length - 1].length - 1]?.status?.color || "neutral";

                let separator: JSX.Element | null = null;
                if (index < this.props.stages.length - 1) {
                    if (isParallel && nextIsParallel) {
                        separator = <BetweenParallelLine color={color} />;
                    }
                    else if (isParallel) {
                        separator = <FromParallelLine color={color} />;
                    }
                    else if (nextIsParallel) {
                        separator = <ToParallelLine color={color} />
                    }
                    else {
                        separator = <NormalLine color={color} />
                    }
                }

                return <span className="flex-row flex-center" key={index}>
                    <span className={css("flex-row", isParallel && "stage-graph-parallel", isParallel && color)} key={"stages_" + index}>
                        <Stages size={this.props.size} stages={stages} />
                    </span>
                    {separator}
                </span>
            })}
        </div>
    }
}

interface StagesComponentProps {
    stages: StageGraphNode[][];
    size: StatusSize;
}

class Stages extends React.Component<StagesComponentProps> {
    public render(): JSX.Element {
        return <span className="flex-row">
            {this.props.stages.map((stages, i) => {
                return <span key={"stages_" + i} className="flex-row">
                    {stages.map((stage, index) => {
                        if(stage.hiddenStageCount) {
                            return <StagePill key={"stage_" + index} hiddenStageCount={stage.hiddenStageCount} size={this.props.size} {...stage} />
                        } else {
                            return <Stage key={"stage_" + index} size={this.props.size} showSeparator={index < stages.length - 1} {...stage} />
                        }
                    })}
                    {(i < this.props.stages.length - 1) && <span className="stage-graph-empty-separator">&nbsp;</span>}
                </span>
            })}
        </span>
    }
}

interface StageProps extends StageGraphNode {
    size: StatusSize;
    showSeparator: boolean;
}

interface StagePillProps extends StageGraphNode {
    size: StatusSize;
    hiddenStageCount: number;
}

class Stage extends VssComponent<StageProps> {
    public render(): JSX.Element {
        if(!this.props.status) {
            return <></>
        }

        return (
            <span className="flex-row flex-center" >
                <Tooltip
                    anchorOrigin={{ horizontal: Location.start, vertical: Location.end }}
                    text={this.props.tooltip}
                    tooltipOrigin={{ horizontal: Location.start, vertical: Location.start }}
                >
                    <span className="flex-row flex-center stage-status">
                        {
                            this.props.href &&
                            <FPSLink subtle={true} excludeTabStop={true} href={this.props.href} className="flex-row flex-center no-h-padding" telemetrySource={this.props.telemetrySource} telemetryProperties={this.props.telemetryProperties}>
                                <Status size={this.props.size} {...this.props.status} ariaLabel={this.props.ariaLabel} />
                            </FPSLink>
                        }
                        {
                            !this.props.href &&
                            <span className="flex-row flex-center no-h-padding">
                                <Status size={this.props.size} {...this.props.status} aria-label={this.props.ariaLabel} />
                            </span>
                        }
                    </span>
                </Tooltip>
                {this.props.showSeparator && <NormalLine color={this.props.status.color} />}
            </span >
        );
    }
}

class StagePill extends VssComponent<StagePillProps> {
    public render(): JSX.Element {
        const color = this.props?.status?.color || "neutral"

        return (
            <span className="flex-row flex-center" onClick={(e) => onClickEvent(e, this.props.onClick)} aria-label={this.props.ariaLabel} role="button">
                <Tooltip
                    anchorOrigin={{ horizontal: Location.start, vertical: Location.end }}
                    text={this.props.tooltip}
                    tooltipOrigin={{ horizontal: Location.start, vertical: Location.start }}
                >
                    <span className={css("flex-row", "flex-center", "status-pill", "border", color)}>
                        <span className="stage-pill-count">+{this.props.hiddenStageCount}</span>
                        {
                            this.props.status && 
                            <Status size={this.props.size} {...this.props.status} aria-label={this.props.status.ariaLabel} />
                        }
                    </span>
                </Tooltip>
            </span >
        );
    }
}

interface LineProps {
    color: string;
}

const NormalLine = (props: LineProps): JSX.Element => <SvgLine color={props.color} width={10} left={1} right={5} />;

const ToParallelLine = (props: LineProps): JSX.Element => <SvgLine color={props.color} width={6} left={3} right={6} />

const FromParallelLine = (props: LineProps): JSX.Element => <SvgLine color={props.color} width={6} left={0} right={3} />

const BetweenParallelLine = (props: LineProps): JSX.Element => <SvgLine color={props.color} width={2} left={0} right={2} viewBox="0 0 2 2" />

interface SvgLineProps {
    color: string;
    width: number;
    left: number;
    right: number;
    viewBox?: string;
}

class SvgLine extends React.Component<SvgLineProps> {
    public render(): JSX.Element {
        return <svg className={css("bolt-status flex-noshrink", this.props.color)} width={this.props.width} height="2" viewBox={this.props.viewBox || "0 0 6 2"} fill="none" xmlns="http://www.w3.org/2000/svg">
            <line x1={this.props.left} y1="1" x2={this.props.right} y2="1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
    }
}

const onClickEvent = (e: React.MouseEvent<HTMLSpanElement, MouseEvent>, onClick?: () => void): void => {
   e.preventDefault();
   onClick && onClick();
}


const getStageCountDigitCoefficient = (pillStageCount: number) => {
    const pillStageCountDigits  = pillStageCount.toString().length;
    return pillStageCountDigits > 2 ? 10 * (pillStageCountDigits - 2) : 0 // When stage count digit number gets greater than 2 add extra 10 pixels for each new digit.
}

export const calculateVisibleStageCount = (totalStageCount: number, availableSpaceWidth?: number, defaultVisibleStagesCount?: number): number => {
    const stageIconWithDividerWidth = parseInt(StatusSize.m) + 10; // Status size plus max line length
    const averagePillSize = 60; // Harcoded average pill size in pixels with two digits and a status
    
    if (!availableSpaceWidth) {
        return defaultVisibleStagesCount || totalStageCount;
    }

    // 1. Check if enough space in screen for all of the stages if so no pill is required
    const maxVisibleStageCount = Math.floor(availableSpaceWidth / stageIconWithDividerWidth);
    if (maxVisibleStageCount >= totalStageCount) {
        return totalStageCount;
    }

    // 2. Calculate aproximate estimation of the pill size for the stage count that needs to be hidden
    const estimatedPillSize = averagePillSize + getStageCountDigitCoefficient(totalStageCount - maxVisibleStageCount);
    
    // 4. Calculate count how many stages could be shown together with the stage pill
    return Math.floor((availableSpaceWidth - estimatedPillSize) / stageIconWithDividerWidth);
}
